import { useEffect, useMemo, useState, useRef } from "react";
import ReactDOM from "react-dom";
import {
  Pencil,
  PencilOff,
  BotOff,
  MessageSquare,
  Mic,
  User,
  Users,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  useSpaceStore,
  type AiMode,
  type WriteMode,
  type CollabMode,
  type ParticipantStatus,
  type SpaceParticipant,
} from "../store/useSpaceStore";
import { useSpace } from "../hooks/useSpace";
import { getAvatarColor } from "../spacesPresenceUtils";
import { formatDuration } from "../spacesTimeUtils";
import { ParticipantListSkeleton } from "./SpaceLoadingSkeletons";
import {
  useNotificationStore,
  NotificationType,
} from "@/store/useNotificationStore";
import "../space-live.css";

const WRITE_SEGMENTS: {
  mode: WriteMode;
  icon: React.ReactNode;
  title: string;
  textClass: string;
  indicatorBg: string;
}[] = [
  {
    mode: "read",
    icon: <PencilOff className="w-3.5 h-3.5" />,
    title: "View only",
    textClass: "text-red-500",
    indicatorBg: "#fee2e2",
  },
  {
    mode: "edit",
    icon: <Pencil className="w-3.5 h-3.5" />,
    title: "Can draw",
    textClass: "text-blue-600",
    indicatorBg: "#dbeafe",
  },
];

const COLLAB_SEGMENTS: {
  mode: CollabMode;
  icon: React.ReactNode;
  title: string;
  textClass: string;
  indicatorBg: string;
}[] = [
  {
    mode: "local",
    icon: <User className="w-3.5 h-3.5" />,
    title: "Personal canvas",
    textClass: "text-amber-600",
    indicatorBg: "#fef3c7",
  },
  {
    mode: "shared",
    icon: <Users className="w-3.5 h-3.5" />,
    title: "Shared canvas",
    textClass: "text-emerald-600",
    indicatorBg: "#d1fae5",
  },
];

const AI_SEGMENTS: {
  mode: AiMode;
  icon: React.ReactNode;
  title: string;
  textClass: string;
  indicatorBg: string;
}[] = [
  {
    mode: "off",
    icon: <BotOff className="w-3.5 h-3.5" />,
    title: "AI off",
    textClass: "text-red-500",
    indicatorBg: "#fee2e2",
  },
  {
    mode: "text-only",
    icon: <MessageSquare className="w-3.5 h-3.5" />,
    title: "Text only",
    textClass: "text-amber-600",
    indicatorBg: "#fef3c7",
  },
  {
    mode: "voice",
    icon: <Mic className="w-3.5 h-3.5" />,
    title: "Voice",
    textClass: "text-emerald-600",
    indicatorBg: "#d1fae5",
  },
];

const COLLAB_MODE_TEACHER_TOAST: Record<CollabMode, string> = {
  local: "Canvas private — each student draws on their own canvas.",
  shared: "Canvas shared — students' drawings are visible to everyone.",
};

const AI_MODE_TEACHER_TOAST: Record<AiMode, string> = {
  off: "AI is off — students can't talk to the AI.",
  "text-only": "Chat only — students reach the AI via text chat.",
  voice: "Voice on — students reach the AI through the mic.",
};

// Each button is w-7 (28px) + gap-0.5 (2px) = 30px per step
const STEP_PX = 30;

type Segment<T extends string> = {
  mode: T;
  icon: React.ReactNode;
  title: string;
  textClass: string;
  indicatorBg: string;
};

function SegmentedControl<T extends string>({
  segments,
  value,
  onChange,
  className,
}: {
  segments: Segment<T>[];
  value: T;
  onChange: (mode: T) => void;
  className?: string;
}) {
  const index = segments.findIndex((s) => s.mode === value);
  const activeSeg = segments[Math.max(0, index)];
  return (
    <div
      className={`relative flex items-center rounded-lg bg-gray-100 p-0.5 gap-0.5 ${className ?? ""}`}
    >
      <div
        className="absolute left-0.5 top-0.5 bottom-0.5 w-7 rounded-md shadow-sm transition-transform duration-200 ease-in-out"
        style={{
          transform: `translateX(${Math.max(0, index) * STEP_PX}px)`,
          backgroundColor: activeSeg.indicatorBg,
        }}
      />
      {segments.map((seg) => (
        <button
          key={seg.mode}
          onClick={() => {
            if (seg.mode !== value) onChange(seg.mode);
          }}
          className={`group relative z-10 w-7 h-7 flex items-center justify-center rounded-md transition-colors duration-200 ${
            value === seg.mode
              ? seg.textClass
              : "text-gray-300 hover:text-gray-400"
          }`}
        >
          {seg.icon}
          <span className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-1.5 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-[11px] leading-none text-white opacity-0 transition-opacity group-hover:opacity-100 z-50">
            {seg.title}
          </span>
        </button>
      ))}
    </div>
  );
}

function statusLabel(status: ParticipantStatus | undefined): string {
  if (!status) return "";
  if (status === "just_joined") return "Just joined";
  if (status === "working") return "Working";
  if (status === "present") return "Present";
  return "Idle";
}

function ParticipantRow({
  userId,
  name,
  status,
  lastJoinedAt,
  timeSpentMs,
}: {
  userId: number;
  name: string;
  status: ParticipantStatus | undefined;
  lastJoinedAt: Date | undefined;
  timeSpentMs: number | undefined;
}) {
  const [elapsed, setElapsed] = useState("");
  const [tooltipPos, setTooltipPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const avatarRef = useRef<HTMLDivElement>(null);
  const avatarColor = getAvatarColor(userId);
  const isIdle = !status || status === "idle";
  const isPresent = status === "present";

  useEffect(() => {
    if (!lastJoinedAt) return;
    const base = timeSpentMs ?? 0;
    const tick = () =>
      setElapsed(
        formatDuration(base + (Date.now() - lastJoinedAt.getTime()), false),
      );
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lastJoinedAt, timeSpentMs]);

  const label = statusLabel(status);
  const dotColor = isIdle
    ? "bg-gray-300"
    : isPresent
      ? "bg-blue-400"
      : "bg-emerald-400";
  const textColor = isIdle
    ? "text-gray-300"
    : isPresent
      ? "text-blue-400"
      : "text-emerald-400";

  const handleMouseEnter = () => {
    const rect = avatarRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTooltipPos({ top: rect.top - 8, left: rect.left + rect.width / 2 });
  };

  const handleMouseLeave = () => setTooltipPos(null);

  const tooltip = tooltipPos
    ? ReactDOM.createPortal(
        <div
          className="pointer-events-none fixed z-[9999] -translate-x-1/2 -translate-y-full"
          style={{ top: tooltipPos.top, left: tooltipPos.left }}
        >
          <div className="bg-gray-800 text-white rounded-lg px-3 py-2.5 text-xs whitespace-nowrap shadow-lg min-w-[160px]">
            <p className="font-semibold text-[13px] leading-snug">{name}</p>
            <hr className="border-gray-600 my-1.5" />
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-6">
                <span className="text-gray-400">Status</span>
                <span className={`font-medium ${textColor}`}>
                  {label || "Idle"}
                </span>
              </div>
              {elapsed && (
                <div className="flex items-center justify-between gap-6">
                  <span className="text-gray-400">Time spent</span>
                  <span className="font-medium tabular-nums">{elapsed}</span>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className="flex items-center gap-2.5 min-w-0 flex-1">
      {/* Avatar with status dot */}
      <div
        ref={avatarRef}
        className="relative shrink-0 cursor-default"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
          style={{ backgroundColor: avatarColor.bg, color: avatarColor.text }}
        >
          {name.charAt(0).toUpperCase()}
        </div>
        {status && (
          <span
            className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${dotColor}`}
          />
        )}
        {tooltip}
      </div>

      {/* Name + status row */}
      <div className="flex flex-col min-w-0">
        <span className="text-sm font-mulish text-gray-700 truncate leading-tight">
          {name}
        </span>
        {label && (
          <span
            className={`text-[10px] font-mulish ${isIdle ? "text-gray-400" : isPresent ? "text-blue-500" : "text-emerald-600"} leading-tight mt-0.5`}
          >
            {label}
          </span>
        )}
      </div>
    </div>
  );
}

export default function ParticipantPanel({
  watchedStudentId,
  onWatchStudent,
}: {
  watchedStudentId?: string | null;
  onWatchStudent?: (id: string | null) => void;
}) {
  const spaceId = useSpaceStore((s) => s.spaceId);
  const participants = useSpaceStore((s) => s.participants);
  const participantsLoaded = useSpaceStore((s) => s.participantsLoaded);
  const connectedUserIds = useSpaceStore((s) => s.connectedUserIds);
  const aiMode = useSpaceStore((s) => s.aiMode);
  const collabMode = useSpaceStore((s) => s.collabMode);
  const totalEnrolled = useSpaceStore((s) => s.totalEnrolled);
  const { updateParticipantWriteMode, updateSettings } = useSpace();
  const { showNotification } = useNotificationStore();

  const connectedParticipants = useMemo(
    () => participants.filter((p) => connectedUserIds.has(p.userId)),
    [participants, connectedUserIds],
  );
  const denominator = totalEnrolled > 0 ? totalEnrolled : participants.length;
  const attendancePct =
    denominator > 0 ? (connectedParticipants.length / denominator) * 100 : 0;

  // Group connected participants by their first classroom (alphabetical order
  // is set by the backend) so a multi-class space can render dividers per class.
  // A student that belongs to multiple classrooms in this space appears under
  // the first one only — by design, no duplication. Students without any
  // classroom membership land in an "Unassigned" group rendered last.
  // We skip dividers entirely when the connected list spans a single group.
  const groupedParticipants = useMemo(() => {
    type Group = {
      id: number | "unassigned";
      name: string;
      participants: SpaceParticipant[];
    };
    const groups = new Map<number | "unassigned", Group>();

    for (const p of connectedParticipants) {
      const primary = p.classrooms?.[0];
      const key: number | "unassigned" = primary ? primary.id : "unassigned";
      const name = primary ? primary.name : "Unassigned";
      const group = groups.get(key) ?? { id: key, name, participants: [] };
      group.participants.push(p);
      groups.set(key, group);
    }

    const ordered = [...groups.values()];
    ordered.sort((a, b) => {
      // "Unassigned" always last; otherwise alphabetical by classroom name.
      if (a.id === "unassigned") return 1;
      if (b.id === "unassigned") return -1;
      return a.name.localeCompare(b.name);
    });
    for (const group of ordered) {
      group.participants.sort((a, b) => a.name.localeCompare(b.name));
    }
    return ordered;
  }, [connectedParticipants]);

  const showClassroomDividers = groupedParticipants.length > 1;

  if (!spaceId) return null;

  return (
    <div className="et-live-chrome w-80 border-none flex flex-col bg-white shrink-0">
      <div className="px-3 py-3 flex flex-row justify-between items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700">
          Students{" "}
          {participantsLoaded ? `(${connectedParticipants.length})` : ""}
        </h3>

        <div className="flex items-center gap-2 shrink-0">
          <SegmentedControl
            segments={COLLAB_SEGMENTS}
            value={collabMode}
            onChange={(mode) =>
              updateSettings(spaceId, { collabMode: mode })
                .then(() =>
                  showNotification(
                    COLLAB_MODE_TEACHER_TOAST[mode],
                    NotificationType.SUCCESS,
                  ),
                )
                .catch(() => {})
            }
          />
          <SegmentedControl
            segments={AI_SEGMENTS}
            value={aiMode}
            onChange={(mode) =>
              updateSettings(spaceId, { aiMode: mode })
                .then(() =>
                  showNotification(
                    AI_MODE_TEACHER_TOAST[mode],
                    NotificationType.SUCCESS,
                  ),
                )
                .catch(() => {})
            }
          />
        </div>
      </div>

      {/* Attendance bar */}
      <div className="px-4 pt-1 pb-2 shrink-0">
        <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
          {participantsLoaded && (
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-500"
              style={{ width: `${attendancePct}%` }}
            />
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {!participantsLoaded ? (
          <ParticipantListSkeleton />
        ) : connectedParticipants.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-400 text-center">
            No students yet
          </p>
        ) : (
          <div>
            {groupedParticipants.map((group) => (
              <section key={group.id}>
                {showClassroomDividers && (
                  <h4 className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400 font-mulish">
                    {group.name}
                  </h4>
                )}
                <ul className="divide-y">
                  {group.participants.map((p) => {
                    const uid = String(p.userId);
                    const isWatched = watchedStudentId === uid;
                    return (
                      <li
                        key={p.userId}
                        className="flex items-center justify-between px-3 py-2.5 gap-2"
                      >
                        <ParticipantRow
                          userId={p.userId}
                          name={p.name}
                          status={p.status}
                          lastJoinedAt={p.lastJoinedAt}
                          timeSpentMs={p.timeSpentMs}
                        />
                        <div className="flex items-center gap-1 ml-1 shrink-0">
                          {onWatchStudent && (
                            <button
                              onClick={() =>
                                onWatchStudent(isWatched ? null : uid)
                              }
                              className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors ${
                                isWatched
                                  ? "text-indigo-600 bg-indigo-50"
                                  : "text-gray-300 hover:text-gray-500 hover:bg-gray-100"
                              }`}
                              title={
                                isWatched ? "Stop watching" : "Watch canvas"
                              }
                            >
                              {isWatched ? (
                                <EyeOff className="w-3.5 h-3.5" />
                              ) : (
                                <Eye className="w-3.5 h-3.5" />
                              )}
                            </button>
                          )}
                          <SegmentedControl
                            segments={WRITE_SEGMENTS}
                            value={p.writeMode ?? "read"}
                            onChange={(mode) =>
                              updateParticipantWriteMode(
                                spaceId,
                                p.userId,
                                mode,
                              )
                                .then(() => {
                                  const copy =
                                    mode === "read"
                                      ? `${p.name}: Drawing stopped.`
                                      : `${p.name}: Drawing enabled.`;
                                  showNotification(
                                    copy,
                                    NotificationType.SUCCESS,
                                  );
                                })
                                .catch(() => {})
                            }
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
